<template>
    <div v-if="isOpen" @click.stop>
        <!-- 배경 클릭 감지 -->
        <div class="fixed inset-0 z-[9998]" @click="close"></div>

        <!-- 드롭다운 (fixed position으로 overflow 문제 해결) -->
        <div ref="dropdownRef"
            class="fixed z-[9999] mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]"
            :style="dropdownPosition">
            <div v-for="label in labels" :key="label.id" @click="toggleLabel(label.id)"
                class="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors">
                <!-- 체크박스 -->
                <div class="w-4 h-4 flex items-center justify-center">
                    <svg v-if="hasLabel(label.id)" class="w-4 h-4 text-blue-600" fill="currentColor"
                        viewBox="0 0 20 20">
                        <path fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd" />
                    </svg>
                </div>

                <!-- 색상 원 -->
                <div class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: label.colorCode }"></div>

                <!-- 라벨 이름 -->
                <span class="text-sm text-gray-700 flex-1">{{ label.name }}</span>
            </div>

            <!-- 라벨이 없을 때 -->
            <div v-if="labels.length === 0" class="px-3 py-2 text-sm text-gray-500 text-center">
                라벨이 없습니다
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch, nextTick } from 'vue';
import { useQuoteLabel } from '@/composables/useQuoteLabel';

export default {
    name: 'LabelSelector',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        creditQuoteId: {
            type: Number,
            required: true,
        },
        position: {
            type: String,
            default: 'bottom-left', // 'bottom-left', 'bottom-right', 'top-left', 'top-right'
        },
    },
    emits: ['update:modelValue', 'label-toggled'],
    setup(props, { emit }) {
        const { labels, toggleLabelForQuote, hasLabel: checkHasLabel } = useQuoteLabel();

        const isOpen = ref(props.modelValue);
        const dropdownRef = ref(null);
        const dropdownPosition = ref({});

        const updatePosition = () => {
            nextTick(() => {
                if (!dropdownRef.value) return;

                // 부모 요소(+ 버튼)의 위치를 가져옴
                const parent = dropdownRef.value.parentElement;
                if (!parent) return;

                const rect = parent.getBoundingClientRect();
                const dropdownHeight = 120; // 예상 높이
                const dropdownWidth = 160;

                let top, left;

                // 아래로 표시할 공간이 충분한지 확인
                if (props.position.includes('bottom')) {
                    top = rect.bottom + 4;
                    // 화면 아래로 넘어가면 위로 표시
                    if (top + dropdownHeight > window.innerHeight) {
                        top = rect.top - dropdownHeight - 4;
                    }
                } else {
                    top = rect.top - dropdownHeight - 4;
                }

                if (props.position.includes('right')) {
                    left = rect.right - dropdownWidth;
                    // 화면 왼쪽으로 넘어가면 조정
                    if (left < 0) {
                        left = rect.left;
                    }
                } else {
                    left = rect.left;
                    // 화면 오른쪽으로 넘어가면 조정
                    if (left + dropdownWidth > window.innerWidth) {
                        left = rect.right - dropdownWidth;
                    }
                }

                dropdownPosition.value = {
                    top: `${top}px`,
                    left: `${left}px`,
                };
            });
        };

        watch(() => props.modelValue, (newValue) => {
            isOpen.value = newValue;
            if (newValue) {
                updatePosition();
            }
        });

        const close = () => {
            isOpen.value = false;
            emit('update:modelValue', false);
        };

        const hasLabel = (labelId) => {
            return checkHasLabel(props.creditQuoteId, labelId);
        };

        const toggleLabel = async (labelId) => {
            try {
                await toggleLabelForQuote(labelId, props.creditQuoteId);
                emit('label-toggled', labelId);
            } catch (error) {
                console.error('라벨 토글 실패:', error);
            }
        };

        return {
            isOpen,
            labels,
            close,
            hasLabel,
            toggleLabel,
            dropdownRef,
            dropdownPosition,
        };
    },
};
</script>

<style scoped>
/* 드롭다운 애니메이션 */
.fixed.z-\[9999\] {
    animation: slideDown 0.15s ease-out;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-4px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
